import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import Pagination from 'react-bootstrap/Pagination';
import { fetchAllProductsMinimal, fetchStoreProductSettings, upsertStoreProductSetting, updateStoreProductSetting } from '../../api/api';
import { useNavigate } from 'react-router-dom';


export default function StoreReorderLevels() {
    const navigate = useNavigate();
    const [allProducts, setAllProducts] = useState([]);
    const [settings, setSettings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [search, setSearch] = useState('');
    const [drafts, setDrafts] = useState({});
    const [message, setMessage] = useState(null);

    const handleBack = () => {
        navigate(-1);
    };

    // pagination (match purchase.js style)
    const [currentPage, setCurrentPage] = useState(1);
    const entriesPerPage = 20;

    const load = async () => {
        setLoading(true);
        try {
            const data = await fetchStoreProductSettings();
            setSettings(data.results || data);
        } catch (e) {
            console.error('Failed to load store product settings:', e);
            setSettings([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const loadProducts = async () => {
            try {
                const data = await fetchAllProductsMinimal();
                setAllProducts(Array.isArray(data) ? data : (data?.results || []));
            } catch (e) {
                console.error('Failed to load products:', e);
                setAllProducts([]);
            }
        };
        loadProducts();
    }, []);

    const settingsByProductId = useMemo(() => {
        const map = new Map();
        settings.forEach((s) => map.set(String(s.product), s));
        return map;
    }, [settings]);

    const filteredProducts = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return allProducts;
        return allProducts.filter((p) => (p.product_name || '').toLowerCase().includes(q));
    }, [allProducts, search]);

    useEffect(() => {
        setCurrentPage(1);
    }, [search]);

    const totalPages = Math.ceil(filteredProducts.length / entriesPerPage) || 1;
    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const maxLinks = 5;
    let startLink = Math.max(currentPage - 2, 1);
    let endLink = Math.min(startLink + maxLinks - 1, totalPages);

    if (endLink > totalPages) {
        endLink = totalPages;
        startLink = Math.max(endLink - (maxLinks - 1), 1);
    }

    const pageNumbersToShow = [];
    for (let i = startLink; i <= endLink; i++) {
        pageNumbersToShow.push(i);
    }

    const paginatedProducts = useMemo(() => {
        const startIndex = (currentPage - 1) * entriesPerPage;
        return filteredProducts.slice(startIndex, startIndex + entriesPerPage);
    }, [currentPage, filteredProducts]);

    const getDisplayedValue = (product) => {
        const draft = drafts[String(product.id)];
        if (draft !== undefined) return draft;
        const setting = settingsByProductId.get(String(product.id));
        if (setting) return setting.reorder_level;
        return product.reorder_level ?? '';
    };

    const saveOne = async (product) => {
        const productId = String(product.id);
        const value = drafts[productId];
        if (value === undefined || value === '') {
            setMessage({ type: 'warning', text: 'Please enter a reorder level value before saving.' });
            return;
        }

        setSaving(true);
        setMessage(null);
        try {
            const existing = settingsByProductId.get(productId);
            if (existing?.id) {
                await updateStoreProductSetting(existing.id, { reorder_level: value });
            } else {
                await upsertStoreProductSetting(product.id, value);
            }
            await load();
            setMessage({ type: 'success', text: 'Reorder level saved.' });
        } catch (e) {
            console.error('Failed to save reorder level:', e);
            setMessage({ type: 'danger', text: 'Failed to save reorder level.' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="container-fluid py-4">
            <div className="back-button-container">
                <Button variant="danger" onClick={handleBack}>Back</Button>
            </div>
            <Row className="mb-3">
                <Col>
                    <h3 className="fw-bold mb-1">Reorder Levels (This Store)</h3>
                    <div className="text-muted">Set product reorder thresholds for your active store.</div>
                </Col>
            </Row>

            {message && (
                <Alert variant={message.type} onClose={() => setMessage(null)} dismissible>
                    {message.text}
                </Alert>
            )}

            <Card className="shadow-sm border-0">
                <Card.Body>
                    <Row className="g-3 align-items-end mb-3">
                        <Col md={6}>
                            <Form.Label>Search product</Form.Label>
                            <Form.Control value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Type a product name..." />
                        </Col>
                        <Col md={6} className="text-md-end">
                            <Button variant="outline-secondary" onClick={load} disabled={loading || saving}>
                                Refresh
                            </Button>
                        </Col>
                    </Row>

                    {loading ? (
                        <Spinner animation="border" />
                    ) : (
                        <Table responsive hover>
                            <thead>
                                <tr>
                                    <th>Product</th>
                                    <th style={{ width: 220 }}>Reorder Level</th>
                                    <th style={{ width: 140 }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedProducts.length ? paginatedProducts.map((p) => (
                                    <tr key={p.id}>
                                        <td>{p.product_name}</td>
                                        <td>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={getDisplayedValue(p)}
                                                onChange={(e) => setDrafts((prev) => ({ ...prev, [String(p.id)]: e.target.value }))}
                                            />
                                        </td>
                                        <td>
                                            <Button
                                                size="sm"
                                                variant="primary"
                                                disabled={saving}
                                                onClick={() => saveOne(p)}
                                            >
                                                Save
                                            </Button>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr>
                                        <td colSpan={3} className="text-center text-muted">No products found</td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    )}

                    <div className="mt-4 d-flex justify-content-center">
                        <Pagination>
                            <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
                            <Pagination.Prev
                                onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
                                disabled={currentPage === 1}
                            />
                            {pageNumbersToShow.map((page) => (
                                <Pagination.Item
                                    key={page}
                                    active={page === currentPage}
                                    onClick={() => handlePageChange(page)}
                                >
                                    {page}
                                </Pagination.Item>
                            ))}
                            <Pagination.Next
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                            />
                            <Pagination.Last
                                onClick={() => handlePageChange(totalPages)}
                                disabled={currentPage === totalPages}
                            />
                        </Pagination>
                    </div>
                </Card.Body>
            </Card>
        </div>
    );
}
